import {useCallback, useEffect, useRef, useState} from 'react';
import fallback from '../shows/members-night.json';

const API = '/engine';

const withClip = (block, clipId) => {
    const segs = block.segments || [], last = segs.at(-1);
    if (!last) return [{kind: 'song', from: 0, clips: [clipId]}];
    if (last.kind === 'song') return [...segs.slice(0, -1), {...last, clips: [...last.clips, clipId]}];
    if (last.from + 1 >= block.scenes.length) throw new Error('add a scene after the video for the song to start on');
    return [...segs, {kind: 'song', from: last.from + 1, clips: [clipId]}];
};

// The show document as the engine holds it (edits write through the engine), refetched whenever the
// engine says it changed. Falls back to the built-in copy when no engine is reachable.
export const useShow = (lastEvent) => {
    const [doc, setDoc] = useState(fallback);
    const refetch = useCallback(() => {
        fetch(`${API}/show`).then(r => r.json()).then(d => { if (d && d.blocks) setDoc(d); }).catch(() => {});
    }, []);
    useEffect(() => { refetch(); }, [refetch]);
    useEffect(() => { if (lastEvent && lastEvent.type === 'show-updated') refetch(); }, [lastEvent, refetch]);

    const call = useCallback((method, path, body) => fetch(`${API}${path}`, {
        method, headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body),
    }).then(r => r.json()).then(res => { if (res && res.error) throw new Error(res.error); refetch(); return res; }), [refetch]);

    return {
        doc, refetch,
        patchBlock: (id, patch) => call('PATCH', `/show/blocks/${encodeURIComponent(id)}`, patch),
        setSceneTimes: (id, times) => call('PUT', `/show/blocks/${encodeURIComponent(id)}/scene-times`, times),
        saveClip: (clip) => call('POST', '/clips', clip),
        trimMusic: (id, k, patch) => call('PUT', `/show/blocks/${encodeURIComponent(id)}/music/${k}`, patch),
        // A clip onto a block: it joins the block's last song, or starts the block's media when it has none.
        addClip: (block, clipId) => call('PATCH', `/show/blocks/${encodeURIComponent(block.id)}`, {segments: withClip(block, clipId)}),
        // A whole song onto a block: a new clip spanning all of it.
        addSong: async (block, media, name) => {
            const slug = (name || 'song').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
            const clip = await call('POST', '/clips', {id: `${slug}-${Date.now().toString(36)}`, name, media});
            return call('PATCH', `/show/blocks/${encodeURIComponent(block.id)}`, {segments: withClip(block, clip.id)});
        },
        // k counts song clips in block order; a song left with none is dropped.
        removeClip: (block, k) => {
            let n = 0;
            const segments = (block.segments || []).map(g => g.kind !== 'song' ? g : {...g, clips: g.clips.filter(() => n++ !== k)}).filter(g => g.kind !== 'song' || g.clips.length);
            return call('PATCH', `/show/blocks/${encodeURIComponent(block.id)}`, {segments});
        },
    };
};

// Waveform peaks per media id, fetched once and shared.
const peaksCache = new Map();
export const useAnalyses = (mediaIds) => {
    const [version, bump] = useState(0);
    const wanted = useRef('');
    const key = [...new Set(mediaIds)].sort().join(',');
    useEffect(() => {
        if (key === wanted.current) return;
        wanted.current = key;
        key.split(',').filter(Boolean).forEach(id => {
            if (peaksCache.has(id)) return;
            peaksCache.set(id, null);
            const src = id.startsWith('video:') ? `${API}/stage-media/peaks/${encodeURIComponent(id.slice(6))}.json` : `${API}/media/${id}/analysis`;
            fetch(src).then(r => r.ok ? r.json() : null)
                .then(a => { if (a && a.peaks) { peaksCache.set(id, a); bump(n => n + 1); } }).catch(() => {});
        });
    }, [key]);
    return {get: (id) => peaksCache.get(id), version};
};
